import { FC, forwardRef } from 'react';

import cn from '@/utils/cn';

import ErrorMessage from './ErrorMessage';
import { InputProps, InputWrapperProps } from './Input.types';
import { cva } from 'class-variance-authority';

export const inputStyle = cva(
  `max-h-[52px] w-full rounded-md px-3.5 pb-2 pt-5.5 lg:pb-1.5
  focus:ring-4 focus:ring-primary/20
  outline outline-1 outline-dark-gray-100 hover:outline-blue-600 focus:outline-blue-600
  transition-[outline-color] duration-200 ease-in-out`,
  {
    variants: {
      error: {
        true: ['outline-red-400 hover:outline-red-400'],
      },
      success: {
        true: [
          'outline-green-500 hover:outline-green-500 focus:outline-green-500',
        ],
      },
      successNeutral: {
        true: [
          'outline-dark-gray-300 hover:outline-dark-gray-300 focus:outline-dark-gray-300',
        ],
      },
      disabled: {
        true: [
          'cursor-not-allowed bg-gray-200 text-dark-gray-400 outline-dark-gray-100 hover:outline-dark-gray-100 focus:outline-dark-gray-100',
        ],
      },
    },
  }
);

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      error,
      success,
      disabled,
      rightSection,
      classNameRightSection,
      value,
      label,
      successNeutral,
      ...props
    },
    ref
  ) => {
    return (
      <InputWrapper
        value={value}
        error={error}
        classNameRightSection={classNameRightSection}
        success={success}
        label={label}
        rightSection={rightSection}
      >
        <input
          className={cn(
            inputStyle({
              error: !!error,
              success: !!success,
              successNeutral: !!successNeutral,
              disabled: disabled,
            }),
            className
          )}
          disabled={disabled}
          ref={ref}
          value={value}
          data-testid={`${props?.name}-input`}
          {...props}
        />
      </InputWrapper>
    );
  }
);

const InputWrapper: FC<InputWrapperProps> = ({
  error,
  success,
  rightSection,
  classNameRightSection,
  value,
  label,
  children,
}) => (
  <>
    <div className={cn('group relative rounded-md')}>
      <span
        className={cn(
          'body pointer-events-none absolute left-3.5 translate-y-1/2 text-dark-gray-200 duration-150',
          value
            ? 'input-label-small'
            : 'group-focus-within:input-label-small leading-[26px]'
        )}
      >
        {label}
      </span>
      {rightSection && (
        <div className={cn('absolute right-3.5 top-4', classNameRightSection)}>
          {rightSection}
        </div>
      )}

      {children}
      {error && !success && <ErrorMessage errorMessage={error} />}
    </div>
  </>
);

Input.displayName = 'Input';

export { Input, InputWrapper };
